// Default import (rather than `import * as`) so this module also loads under
// plain Node ESM, which lets the builder be exercised outside the browser.
import XLSX from "xlsx-js-style";

// ═══════════════════════════════════════════════════════════════════════════
// STYLED XLSX BUILDER
// ═══════════════════════════════════════════════════════════════════════════
// Builds the map list export as a real formatted workbook rather than a dump
// of strings: PP-red frozen header, autofilter, per-type number formats, and
// a totals row written as live Excel formulas so it re-computes when rows are
// deleted in Excel.
//
// Takes plain sheet specs so the caller owns value extraction and this file
// stays free of any knowledge of pins, tabs or lease bases:
//
//   sheets: [{
//     name:    "Lease Comps",
//     columns: [{ label, type }],          // type: text|num|money|psf|pct|date|norm
//     data:    [[value, ...], ...],        // parallel to columns
//     totals:  [{ colIndex, kind, label }] // kind: sum|avg
//   }]

const PP_RED      = "E93529";
const PP_RED_DARK = "C42D22";
const GREY_RULE   = "D8D8D8";

const NUM_FMT = {
  num:   "#,##0",
  money: "$#,##0",
  psf:   "$#,##0.00",
  norm:  "$#,##0.00",
  // Values are stored as 5.8 meaning 5.8%, so the literal % is part of the
  // format rather than a real percent type (which would multiply by 100).
  pct:   '0.0"%"',
};

const NUMERIC_TYPES = new Set(["num", "money", "psf", "pct", "norm"]);
const isNumericType = t => NUMERIC_TYPES.has(t);

// Excel rejects []:*?/\ in sheet names and caps them at 31 characters.
function safeSheetName(name, taken) {
  let base = String(name || "Sheet").replace(/[[\]:*?/\\]/g, "-").slice(0, 31);
  if (!base) base = "Sheet";
  let candidate = base, n = 2;
  while (taken.has(candidate)) {
    const suffix = ` (${n++})`;
    candidate = base.slice(0, 31 - suffix.length) + suffix;
  }
  taken.add(candidate);
  return candidate;
}

function headerStyle(type) {
  return {
    font: { bold: true, sz: 10, color: { rgb: "FFFFFF" } },
    fill: { patternType: "solid", fgColor: { rgb: PP_RED } },
    alignment: { horizontal: isNumericType(type) ? "right" : "left", vertical: "center" },
    border: { bottom: { style: "medium", color: { rgb: PP_RED_DARK } } },
  };
}

function totalStyle(type) {
  return {
    font: { bold: true, sz: 10 },
    alignment: { horizontal: isNumericType(type) ? "right" : "left" },
    border: { top: { style: "thin", color: { rgb: GREY_RULE } } },
  };
}

function columnWidth(col, data, colIndex) {
  let widest = String(col.label).length;
  for (let i = 0; i < data.length; i++) {
    const v = data[i][colIndex];
    if (v == null) continue;
    const len = typeof v === "number"
      ? String(Math.round(v)).length + (col.type === "psf" || col.type === "norm" ? 4 : 2)
      : String(v).length;
    if (len > widest) widest = len;
  }
  return { wch: Math.min(42, Math.max(9, widest + 2)) };
}

export function buildMapWorkbook(sheets) {
  const wb = XLSX.utils.book_new();
  const taken = new Set();
  let appended = 0;

  (sheets || []).forEach(spec => {
    const columns = spec.columns || [];
    const data = spec.data || [];
    if (columns.length === 0) return;

    const aoa = [columns.map(c => c.label), ...data];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Header row
    columns.forEach((c, ci) => {
      const addr = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!ws[addr]) ws[addr] = { t: "s", v: c.label };
      ws[addr].s = headerStyle(c.type);
    });

    // Body: number formats and right alignment on numeric columns
    for (let ri = 0; ri < data.length; ri++) {
      for (let ci = 0; ci < columns.length; ci++) {
        const addr = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        const cell = ws[addr];
        if (!cell) continue;
        const fmtCode = NUM_FMT[columns[ci].type];
        if (fmtCode && typeof cell.v === "number") {
          cell.t = "n";
          cell.z = fmtCode;
          cell.s = { alignment: { horizontal: "right" } };
        }
      }
    }

    const lastDataRow = data.length;               // 0-indexed; header is row 0
    let lastRow = lastDataRow;

    // Totals row, one blank row below the data, written as formulas so it
    // survives the reader deleting rows in Excel.
    const totals = (spec.totals || []).filter(t => t && t.colIndex < columns.length);
    if (totals.length > 0 && data.length > 0) {
      const totalRow = lastDataRow + 2;
      const labelAddr = XLSX.utils.encode_cell({ r: totalRow, c: 0 });
      ws[labelAddr] = { t: "s", v: "TOTAL", s: totalStyle("text") };

      totals.forEach(t => {
        const colLetter = XLSX.utils.encode_col(t.colIndex);
        const range = `${colLetter}2:${colLetter}${lastDataRow + 1}`;
        const fn = t.kind === "avg" ? "AVERAGE" : "SUM";
        const type = columns[t.colIndex].type;
        const addr = XLSX.utils.encode_cell({ r: totalRow, c: t.colIndex });
        ws[addr] = {
          t: "n",
          f: `${fn}(${range})`,
          z: NUM_FMT[type] || "#,##0",
          s: totalStyle(type),
        };
      });
      lastRow = totalRow;
    }

    // aoa_to_sheet sized !ref to the data only — widen it so the totals row
    // is inside the sheet range, otherwise Excel ignores those cells.
    ws["!ref"] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: lastRow, c: columns.length - 1 },
    });

    ws["!cols"] = columns.map((c, ci) => columnWidth(c, data, ci));
    // No frozen header: xlsx-js-style 0.18.5 does not emit <pane> for any of
    // !freeze / !views / !panes — verified empirically, it's a SheetJS Pro
    // feature. The autofilter below covers most of what freezing gives you.
    if (data.length > 0) {
      ws["!autofilter"] = {
        ref: XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: lastDataRow, c: columns.length - 1 },
        }),
      };
    }

    XLSX.utils.book_append_sheet(wb, ws, safeSheetName(spec.name, taken));
    appended += 1;
  });

  return appended > 0 ? wb : null;
}

// Builds and hands the file to the browser. Returns false when there was
// nothing to write so the caller can surface that instead of a silent no-op.
export function downloadMapWorkbook(sheets, fileName) {
  const wb = buildMapWorkbook(sheets);
  if (!wb) return false;
  XLSX.writeFile(wb, fileName);
  return true;
}
